import { i18n } from '@lingui/core';
import { useAdPositionElements } from '@schibsted-nmp/advertising-companion';
import type { SearchResult } from '@schibsted-nmp/search-frontend-clients/quest';
import React from 'react';
import { useSearchData } from '../../data/SearchData';
import { OikotieBanner } from '../advertising/OikotieBanner';
import { ToriBottomBanner } from '../advertising/tori/ToriBottomBanner';
import { FiltersSkipLink } from '../filters/FiltersSkipLink';
import { CategoryDescription } from './CategoryDescription';
import { EndOfPaging } from './EndOfPaging';
import { Pagination } from './Pagination';
import { PolePosition } from './PolePosition';
import { ResultItem } from './ResultItem';
import { ResultsSkipLinkTarget } from './ResultsSkipLink';
import { SavedSearchTeaser } from './SavedSearchTeaser';
import { SortingInfo } from './SortingInfo';
import { ZeroResults } from './ZeroResults';
import { useLayout } from './layout/LayoutStore';

const RESULTS_HEADING_ID = 'results-heading';

const IMAGE_SIZES =
    '(min-width: 1300px) 306px, (min-width: 1010px) 316px, (min-width: 768px) 33vw, (min-width: 480px) 50vw, 100vw';

type ResultListProps = {
    className?: string;
};

export function ResultList(props: ResultListProps) {
    const { className } = props;

    const { layout } = useLayout();
    const { isLoading, matchCount, numResults, searchData } = useSearchData();

    const resultItems = useResultItems(layout, searchData);
    const hasResults = numResults > 0;

    const layoutClass =
        layout === 'grid'
            ? 'grid grid-cols-2 md:grid-cols-3 grid-flow-row-dense gap-16 items-start'
            : 'space-y-16';

    return (
        <section aria-labelledby={RESULTS_HEADING_ID} className={className}>
            <SortingInfo className="mt-16" />
            <FiltersSkipLink />
            <ResultsSkipLinkTarget />
            <div className="sr-only" role="status">
                {i18n.t({
                    id: 'results.status',
                    message: '{count} results',
                    values: { count: matchCount },
                })}
            </div>
            {(hasResults && (
                <div
                    className={`${layoutClass} mt-16 sf-result-list`}
                    // @ts-expect-error Property 'inert' does not exist on type 'DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>'.ts(2322)
                    inert={isLoading ? 'true' : undefined}
                >
                    {resultItems}
                </div>
            )) || <ZeroResults className="my-48" />}
            <ToriBottomBanner />
            <SavedSearchTeaser className="mt-24" />
            <EndOfPaging className="mt-24" />
            <Pagination className="mt-24" />
            <CategoryDescription
                className="mt-24"
                resultsHeadingId={RESULTS_HEADING_ID}
            />
        </section>
    );
}

function useResultItems(layout: string, searchData: SearchResult | null) {
    const resultItems =
        searchData?.docs?.map((ad, idx) => (
            <ResultItem
                ad={ad}
                imageSizes={IMAGE_SIZES}
                key={ad.id}
                position={idx}
            />
        )) || [];

    resultItems.unshift(<PolePosition key="pole-position" />);

    const [bannerPositions, bannerElements] = useAdPositionElements({
        layoutType: layout as 'grid' | 'list',
        type: 'search',
    });

    if (resultItems.length > 40) {
        resultItems.splice(
            40,
            0,
            <OikotieBanner className="col-span-full" key="oikotie-banner" />,
        );
    }

    const adItemsWithBanners = insertBanners(
        resultItems,
        bannerPositions,
        bannerElements,
    );
    return adItemsWithBanners;
}

function insertBanners(
    adItems: React.ReactNode[],
    bannerPositions?: number[],
    bannerElements?: React.ReactNode[],
) {
    if (!bannerPositions || !bannerElements) {
        return adItems;
    }

    return adItems.flatMap((item, index) =>
        bannerPositions.includes(index)
            ? [bannerElements[bannerPositions.indexOf(index)], item]
            : [item],
    );
}
